import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { capabilities, stepWindows } from '@/constants/content'

import {
    clamp,
    mixHex,
    range,
    smoothstep,
    stepped
} from '@/lib/motion/math'

import {
    emptyMetrics,
    enterProgress,
    isNear,
    measureSection,
    pinRaw,
    useEngine
} from '@/lib/motion/section'

import {
    Bracket,
    createMaskDriver,
    createStyleCache
} from '@/components/landing/primitives'

const TITLE = 'Capabilities'

const READOUTS = [
    'Pulse rate 240 kHz, 5 echoes',
    'Dual band 2.4 and 5.8 GHz',
    'Packed 42 x 18 x 11 cm'
]

const PANEL_BACKGROUND = [
    'linear-gradient(rgba(29,42,49,0.045) 1px, transparent 1px)',
    'linear-gradient(90deg, rgba(29,42,49,0.045) 1px, transparent 1px)',
    'radial-gradient(90% 60% at 50% 30%, rgba(255,255,255,0.75) 0%, rgba(255,255,255,0) 70%)',
    'linear-gradient(180deg, #e7eef1 0%, #dbe5e9 100%)'
].join(', ')

const CapabilitiesSection: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const frameRef = useRef<HTMLDivElement>(null)
    const titleRef = useRef<HTMLHeadingElement>(null)
    const ruleRef = useRef<HTMLDivElement>(null)
    const bodyRef = useRef<HTMLDivElement>(null)
    const markerRef = useRef<HTMLSpanElement>(null)
    const letterRefs = useRef<Array<HTMLSpanElement | null>>([])
    const itemRefs = useRef<Array<HTMLLIElement | null>>([])
    const valueRefs = useRef<Array<HTMLParagraphElement | null>>([])
    const textRefs = useRef<Array<HTMLParagraphElement | null>>([])
    const readoutRefs = useRef<Array<HTMLSpanElement | null>>([])

    useEngine(() => {
        const set = createStyleCache()
        const mask = createMaskDriver(titleRef.current)

        let metrics = emptyMetrics()
        let itemOffsets: Array<number> = []

        return {
            measure: () => {
                if (sectionRef.current) metrics = measureSection(sectionRef.current, frameRef.current)

                itemOffsets = itemRefs.current.map(item => (item ? item.offsetTop + item.offsetHeight / 2 : 0))
            },
            frame: (state) => {
                if (!isNear(metrics, state.scroll, state.vh)) return

                const enter = enterProgress(metrics, state.scroll, state.vh)
                const raw = pinRaw(metrics, state.scroll)
                const s = stepped(clamp(raw), stepWindows.capabilities)

                mask(smoothstep(0.3, 1, enter))

                set(ruleRef.current, 'transform', `scaleX(${smoothstep(0.4, 1, enter).toFixed(4)})`)
                set(bodyRef.current, 'opacity', smoothstep(0.5, 0.95, enter).toFixed(3))

                const wave = range(0.8, 1.14, raw)
                const softness = 3.5
                const head = wave * (TITLE.length + softness)

                letterRefs.current.forEach((letter, index) => {
                    set(letter, 'color', mixHex('#1d2a31', '#d3d9dc', clamp((head - index) / softness)))
                })

                itemRefs.current.forEach((item, index) => {
                    set(item, 'color', mixHex('#b4bcc0', '#1d2a31', clamp(1 - Math.abs(s - index))))
                })

                if (itemOffsets.length === capabilities.length) {
                    const from = Math.min(capabilities.length - 2, Math.floor(s))
                    const f = clamp(s - from)
                    const y = itemOffsets[from] + (itemOffsets[from + 1] - itemOffsets[from]) * f

                    set(markerRef.current, 'transform', `translate3d(0, ${y.toFixed(1)}px, 0)`)
                }

                capabilities.forEach((_, index) => {
                    const d = s - index
                    const opacity = clamp(1 - Math.abs(d) * 2.2).toFixed(3)
                    const blur = Math.min(10, Math.abs(d) * 16).toFixed(2)
                    const visibility = Math.abs(d) < 0.5 ? 'visible' : 'hidden'

                    set(valueRefs.current[index], 'opacity', opacity)
                    set(valueRefs.current[index], 'transform', `translate3d(0, ${(-d * 46).toFixed(1)}px, 0)`)
                    set(valueRefs.current[index], 'filter', `blur(${blur}px)`)
                    set(valueRefs.current[index], 'visibility', visibility)

                    set(textRefs.current[index], 'opacity', opacity)
                    set(textRefs.current[index], 'transform', `translate3d(0, ${(-d * 30).toFixed(1)}px, 0)`)
                    set(textRefs.current[index], 'filter', `blur(${(Number(blur) * 0.6).toFixed(2)}px)`)
                    set(textRefs.current[index], 'visibility', visibility)

                    set(readoutRefs.current[index], 'opacity', opacity)
                })
            }
        }
    })

    return (
        <section id='capabilities' ref={sectionRef} aria-labelledby='capabilities-title' className='relative h-[360vh] md:h-[380vh]'>
            <div ref={frameRef} data-frame='' className='sticky top-0 flex h-svh flex-col overflow-hidden px-4 pb-12 pt-[68px] md:px-10 md:pb-14 md:pt-[72px]'>
                <h2 id='capabilities-title' ref={titleRef} aria-label={TITLE} className='display-giant shrink-0 text-[clamp(52px,min(13vw,20vh),232px)]'>
                    <span className='block overflow-hidden pb-[0.06em]'>
                        <span data-mask-inner='' aria-hidden='true' className='mask-inner block will-change-transform'>
                            {TITLE.split('').map((letter, index) => (
                                <span
                                    key={`${letter}-${index}`}
                                    ref={(element) => { letterRefs.current[index] = element }}
                                    style={{ color: '#1d2a31' }}
                                >
                                    {letter}
                                </span>
                            ))}
                        </span>
                    </span>
                </h2>

                <div ref={ruleRef} aria-hidden='true' className='mt-3 h-px w-full shrink-0 origin-left bg-line md:mt-5' />

                <div ref={bodyRef} className='grid min-h-0 flex-1 grid-rows-[auto_minmax(0,1fr)_auto] gap-4 pt-4 md:grid-cols-[minmax(0,1fr)_minmax(0,0.85fr)_minmax(0,1fr)] md:grid-rows-1 md:gap-10 md:pt-8'>
                    <div className='relative'>
                        <span
                            ref={markerRef}
                            aria-hidden='true'
                            className='absolute left-0 top-0 hidden h-px w-6 -translate-y-1/2 bg-ink md:block'
                        />

                        <ol className='flex gap-5 md:flex-col md:gap-5 md:pl-10'>
                            {capabilities.map((capability, index) => (
                                <li
                                    key={capability.id}
                                    ref={(element) => { itemRefs.current[index] = element }}
                                    className='flex items-baseline gap-2 md:gap-4'
                                    style={{ color: index === 0 ? '#1d2a31' : '#b4bcc0' }}
                                >
                                    <span className='label-mono hidden md:inline'>0{index + 1}</span>
                                    <span className='text-[17px] tracking-[-0.03em] md:text-[clamp(24px,2.4vw,40px)] md:font-light'>
                                        {capability.label}
                                    </span>
                                </li>
                            ))}
                        </ol>
                    </div>

                    <div
                        data-anchor='capabilities-panel'
                        className='relative min-h-[240px] overflow-hidden rounded-[22px] border border-white/70 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]'
                        style={{ background: PANEL_BACKGROUND, backgroundSize: '28px 28px, 28px 28px, 100% 100%, 100% 100%' }}
                    >
                        <Bracket className='absolute left-4 top-4 text-ink/70'>Live view</Bracket>

                        <span aria-hidden='true' className='absolute right-4 top-4 flex items-center gap-2'>
                            <span className='size-1.5 animate-pulse rounded-full bg-glacier' />
                            <span className='label-mono text-ink/60'>Rec</span>
                        </span>

                        <div className='absolute inset-x-4 bottom-4 grid'>
                            {READOUTS.map((readout, index) => (
                                <span
                                    key={readout}
                                    ref={(element) => { readoutRefs.current[index] = element }}
                                    className='label-mono text-ink/70 [grid-area:1/1]'
                                    style={{ opacity: index === 0 ? 1 : 0 }}
                                >
                                    {readout}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div className='grid content-start md:content-center'>
                        {capabilities.map((capability, index) => (
                            <div key={capability.id} className='[grid-area:1/1]'>
                                <p
                                    ref={(element) => { valueRefs.current[index] = element }}
                                    className='text-[clamp(48px,7.5vw,136px)] font-light leading-[0.9] tracking-[-0.06em] text-ink'
                                    style={{ opacity: index === 0 ? 1 : 0 }}
                                >
                                    {capability.value}
                                </p>

                                <p
                                    ref={(element) => { textRefs.current[index] = element }}
                                    className='mt-3 max-w-[380px] text-[15px] leading-[1.5] tracking-[-0.01em] text-haze md:mt-6 md:text-[clamp(15px,1.15vw,18px)]'
                                    style={{ opacity: index === 0 ? 1 : 0 }}
                                >
                                    {capability.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                <Bracket className='absolute bottom-4 left-4 text-haze md:bottom-6 md:left-10'>Tech</Bracket>
                <Bracket className='absolute bottom-4 right-4 text-haze md:bottom-6 md:right-10'>Stats</Bracket>
            </div>
        </section>
    )
}

export default CapabilitiesSection
